import React, { useCallback, useState } from "react";
import {
  Table as MuiTable,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Checkbox,
  CircularProgress,
  Paper,
  TableSortLabel,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { t } from "i18next";
import { SortOrder, TableProps } from "types";

const getTableCellWidth = (width: number | undefined): string | undefined =>
  width !== undefined ? `${width * 10}%` : undefined;

export const Table = <T,>({
  columns,
  data,
  sort,
  onSort,
  selectable,
  selected,
  onSelect,
  isLoading,
  renderRowDetails,
  sx
}: TableProps<T> & { renderRowDetails?: (row: T) => React.ReactNode, sx?: any }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const onSortColumn = useCallback(
    (columnName: string): void => {
      if (onSort && sort !== undefined)
        onSort(
          (() => {
            if (sort === null || sort.columnName !== columnName)
              return { columnName, direction: SortOrder.ASC };
            else if (sort?.direction === SortOrder.ASC)
              return { columnName, direction: SortOrder.DESC };
            return null;
          })()
        );
    },
    [onSort, sort]
  );

  return (
    <TableContainer
      component={Paper}
      sx={{ position: "relative", overflow: "auto", ...sx }}
    >
      <MuiTable stickyHeader sx={sx}>
        <TableHead>
          <TableRow>
            {[
              ...(selectable
                ? [{ name: "selection", label: "", width: 0.1 }]
                : []),
              ...columns,
            ].map(({ name, label, width, sortDisabled, ...column }, key) => (
              <TableCell
                key={key}
                width={getTableCellWidth(width)}
                {...column}
                sortDirection={sort?.direction}
              >
                {sort !== undefined ? (
                  <TableSortLabel
                    onClick={() => onSortColumn(name)}
                    active={sort?.columnName === name}
                    direction={sort?.direction}
                    disabled={sortDisabled}
                  >
                    {label}
                  </TableSortLabel>
                ) : (
                  label
                )}
              </TableCell>
            ))}
            {renderRowDetails && <TableCell />}
          </TableRow>
        </TableHead>
        <TableBody>
          {!data.length || isLoading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length + (selectable ? 1 : 0) + (renderRowDetails ? 1 : 0)}
                align="center"
              >
                {isLoading ? (
                  <CircularProgress color="primary" />
                ) : (
                  t("alert:data_empty")
                )}
              </TableCell>
            </TableRow>
          ) : (
            data.map(({ item, values, ...row }, i) => {
              const isSelected = selected?.some(
                (selectedItem) => selectedItem === item
              );
              const isOpen = expanded.has(i);
              return (
                <React.Fragment key={i}>
                  <TableRow {...row}>
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={!!isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => onSelect && onSelect(item)}
                        />
                      </TableCell>
                    )}
                    {values.map(({ value, ...props }, index) => (
                      <TableCell
                        key={index}
                        width={getTableCellWidth(columns.at(index)?.width)}
                        {...props}
                      >
                        {value}
                      </TableCell>
                    ))}
                    {renderRowDetails && (
                      <TableCell>
                        <IconButton size="small" onClick={() => {
                          setExpanded(prev => {
                            const next = new Set(prev);
                            if (next.has(i)) next.delete(i); else next.add(i);
                            return next;
                          });
                        }}>
                          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  {renderRowDetails && (
                    <TableRow>
                      <TableCell colSpan={columns.length + (selectable ? 1 : 0) + 1} style={{ padding: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <div style={{ padding: 2, background: "#fafafa" }}>
                            {renderRowDetails(item)}
                          </div>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
};